/**
 * Resumo do cliente: o que sai do bolso, o que entra de crédito e quanto custa.
 *
 * Nasceu da reunião de 16/10 com um escritório de investimentos: cliente de
 * ticket alto não lê quatro cards de cenário — ele pergunta quanto de dinheiro
 * novo coloca, quanto crédito líquido recebe e qual é o custo total. Esses
 * números já estavam todos na simulação; faltava somar e dizer em voz alta.
 *
 * Tudo sai do MESMO resultado de computeSimulacao (simulacao.ts), então tela e
 * PDF não têm como divergir dos cards de cenário.
 *
 * ── CUSTO ──────────────────────────────────────────────────────────────────
 * Custo = tudo o que o cliente desembolsa no plano − o crédito líquido que
 * recebe. Sem seguro, isso é exatamente crédito × taxa de administração, em
 * qualquer cenário: o lance embutido sai do crédito E do saldo, e o lance pago
 * só antecipa saldo. Por isso o custo em % sobre o crédito LÍQUIDO sobe quando
 * há embutido — é o número que o investidor quer ver e que a taxa sozinha
 * esconde.
 *
 * O seguro pós-contemplação incide sobre o saldo devedor, que cai a cada mês.
 * A parcela do card mostra o seguro do primeiro mês; aqui somamos o seguro mês
 * a mês, com o saldo caindo linearmente.
 *
 * ── REAJUSTE ───────────────────────────────────────────────────────────────
 * A segunda leitura corrige crédito, lances e parcelas uma vez por ano pelo
 * índice do segmento (INCC no imóvel, IPCA no veículo — mesma convenção do
 * comparativo.ts): o pagamento do mês j vale × (1+r)^⌊(j−1)/12⌋. O índice é
 * premissa: vem do Banco Central e o vendedor pode trocar.
 */
import type { SimulacaoResult } from './simulacao.ts';

export type CenarioResumo = 'sorteio' | 'lanceFixo' | 'lanceFidelidade' | 'lanceLivre';

export const NOME_CENARIO: Record<CenarioResumo, string> = {
  sorteio: 'Sorteio',
  lanceFixo: 'Lance fixo',
  lanceFidelidade: 'Lance fidelidade',
  lanceLivre: 'Lance livre',
};

export type ContextoResumo = {
  prazo: number;
  /** Taxa de administração total do plano, em % (ex.: 25.5). */
  taxaAdminPct: number;
  mesContemplacao: number;
  /** Reajuste anual de crédito e parcelas, em % (ex.: 6.61). */
  reajusteAaPct: number;
};

export type Leitura = {
  /** Parcelas pagas até contemplar + lance pago em dinheiro. */
  dinheiroNovoAteContemplar: number;
  creditoLiquido: number;
  /** Tudo o que o cliente paga no plano, do primeiro ao último mês. */
  totalPago: number;
  custo: number;
  /** Custo em % do crédito líquido. */
  custoPctDoCredito: number;
};

export type ResumoCliente = {
  cenario: CenarioResumo;
  mesContemplacao: number;
  parcelaAntes: number;
  parcelasAntes: number;
  parcelaDepois: number;
  parcelasDepois: number;
  lancePago: number;
  lanceEmbutido: number;
  lanceEmbutidoPct: number;
  taxaAdminPct: number;
  reajusteAaPct: number;
  /** Valores de hoje, sem reajuste. */
  semReajuste: Leitura;
  /** Crédito, lances e parcelas corrigidos uma vez por ano. */
  comReajuste: Leitura;
};

type DadosCenario = {
  credito: number;
  lancePago: number;
  lanceEmbutido: number;
  lanceEmbutidoPct: number;
  saldo: number;
  seguro: number;
  parcelaDepois: number;
  parcelasDepois: number;
};

const dadosDoCenario = (sim: SimulacaoResult, cenario: CenarioResumo): DadosCenario => {
  switch (cenario) {
    case 'lanceFixo':
      return {
        credito: sim.creditoLanceFixo,
        lancePago: 0,
        lanceEmbutido: sim.valorLanceFixo,
        lanceEmbutidoPct: sim.lanceFixoPercent,
        saldo: sim.saldoDevedorLanceFixo,
        seguro: sim.seguroLanceFixo,
        parcelaDepois: sim.parcelaAPagarLanceFixo,
        parcelasDepois: sim.parcelasRestantesLanceFixo,
      };
    case 'lanceFidelidade':
      return {
        credito: sim.creditoLanceFidelidade,
        lancePago: 0,
        lanceEmbutido: sim.valorLanceFidelidade,
        lanceEmbutidoPct: sim.lanceFidelidadePercent,
        saldo: sim.saldoDevedorLanceFidelidade,
        seguro: sim.seguroLanceFidelidade,
        parcelaDepois: sim.parcelaAPagarLanceFidelidade,
        parcelasDepois: sim.parcelasRestantesLanceFidelidade,
      };
    case 'lanceLivre':
      return {
        credito: sim.creditoLanceLivre,
        lancePago: sim.valorLanceLivre,
        lanceEmbutido: sim.valorLanceEmbutido,
        lanceEmbutidoPct: sim.lanceEmbutidoPercent,
        saldo: sim.saldoDevedorLanceLivre,
        seguro: sim.seguroLanceLivre,
        parcelaDepois: sim.parcelaAPagarLanceLivre,
        parcelasDepois: sim.parcelasRestantesLanceLivre,
      };
    default:
      return {
        credito: sim.creditoSorteio,
        lancePago: 0,
        lanceEmbutido: 0,
        lanceEmbutidoPct: 0,
        saldo: sim.saldoDevedorSorteio,
        seguro: sim.seguroSorteio,
        parcelaDepois: sim.parcelaAPagarSorteio,
        parcelasDepois: sim.parcelasRestantesSorteio,
      };
  }
};

/**
 * Parcela de cada mês do plano num cenário: índice 0 = mês 1. Antes da
 * contemplação, a parcela contratada; depois, a do cenário, com o seguro
 * caindo junto com o saldo. Com reajuste, o mês j vale × (1+r)^⌊(j−1)/12⌋.
 * É a mesma série que soma o resumo e que a tabela "ano a ano" mostra.
 */
export const parcelasDoPlano = (
  sim: SimulacaoResult,
  ctx: ContextoResumo,
  cenario: CenarioResumo,
  comReajuste: boolean,
): number[] => {
  const d = dadosDoCenario(sim, cenario);
  const m = Math.min(Math.max(0, ctx.mesContemplacao), ctx.prazo);
  const n = Math.max(0, d.parcelasDepois);
  const f = comReajuste ? 1 + (Number(ctx.reajusteAaPct) || 0) / 100 : 1;
  const fator = (j: number) => Math.pow(f, Math.floor((j - 1) / 12));

  // Pós: amortização constante + seguro sobre o saldo que cai a cada mês.
  const amortizacao = n > 0 ? Math.max(0, d.saldo) / n : 0;
  const taxaSeguroMes = d.saldo > 0 ? d.seguro / d.saldo : 0;
  const pagamentoPos = (k: number) => amortizacao + taxaSeguroMes * amortizacao * (n - k + 1);

  const serie: number[] = [];
  for (let j = 1; j <= m; j++) serie.push(sim.parcelaJunta * fator(j));
  for (let k = 1; k <= n; k++) serie.push(pagamentoPos(k) * fator(m + k));
  return serie;
};

export const montarResumoCliente = (
  sim: SimulacaoResult,
  ctx: ContextoResumo,
  cenario: CenarioResumo,
): ResumoCliente => {
  const d = dadosDoCenario(sim, cenario);
  const m = Math.min(Math.max(0, ctx.mesContemplacao), ctx.prazo);
  const n = Math.max(0, d.parcelasDepois);
  const f = 1 + (Number(ctx.reajusteAaPct) || 0) / 100;

  const leitura = (comReajuste: boolean): Leitura => {
    const serie = parcelasDoPlano(sim, ctx, cenario, comReajuste);
    const soma = (de: number, ate: number) => serie.slice(de, ate).reduce((a, b) => a + b, 0);
    const pagoAntes = soma(0, m);
    const pagoDepois = soma(m, serie.length);
    const fatorContemplacao = comReajuste && m > 0 ? Math.pow(f, Math.floor((m - 1) / 12)) : 1;
    const lancePago = d.lancePago * fatorContemplacao;
    const creditoLiquido = d.credito * fatorContemplacao;
    const totalPago = pagoAntes + lancePago + pagoDepois;
    const custo = totalPago - creditoLiquido;
    return {
      dinheiroNovoAteContemplar: pagoAntes + lancePago,
      creditoLiquido,
      totalPago,
      custo,
      custoPctDoCredito: creditoLiquido > 0 ? (custo / creditoLiquido) * 100 : 0,
    };
  };

  return {
    cenario,
    mesContemplacao: m,
    parcelaAntes: sim.parcelaJunta,
    parcelasAntes: m,
    parcelaDepois: d.parcelaDepois,
    parcelasDepois: n,
    lancePago: d.lancePago,
    lanceEmbutido: d.lanceEmbutido,
    lanceEmbutidoPct: d.lanceEmbutidoPct,
    taxaAdminPct: ctx.taxaAdminPct,
    reajusteAaPct: ctx.reajusteAaPct,
    semReajuste: leitura(false),
    comReajuste: leitura(true),
  };
};

/**
 * Cenário que abre o resumo: o primeiro lance que o vendedor marcou para a
 * proposta, do mais agressivo ao mais simples; sem nenhum, o sorteio.
 */
export const cenarioPadrao = (ativos: {
  lanceLivreAtivo?: boolean;
  lanceFixoAtivo?: boolean;
  lanceFidelidadeAtivo?: boolean;
}): CenarioResumo =>
  ativos.lanceLivreAtivo
    ? 'lanceLivre'
    : ativos.lanceFixoAtivo
      ? 'lanceFixo'
      : ativos.lanceFidelidadeAtivo
        ? 'lanceFidelidade'
        : 'sorteio';
